import React, { HTMLProps, useContext, useMemo } from 'react';
import Slider from 'rc-slider';
import { Forecast } from '.';
import { TooltipIndicator } from '../../general/TooltipIndicator';
import Toggle from 'react-toggle';
import {
    ForecastContext,
    ForecastParameters,
    getTotalDeposit,
} from '../../../contexts/investment-robot/ForecastContext';
import { reverseFormatNumber } from '../../../utils/parsers';
import CurrencyInput from 'react-currency-input-field';
import { Tooltip } from '../../general/Tooltip';
import { ForecastChartWrapper } from './ForecastChartWrapper';
import { IconElements } from '../../../config/icons';
import { Pill } from '../../general/Pill';
import { GoalIndicationContext } from '../../../contexts/investment-robot/GoalIndicationContext';
import { PathIcon } from '../../../config/iconSelector';
import { ForecastResultsSticky } from './ForecastResultsSticky';
import { useTranslation } from 'react-i18next';
import { ForecastValue } from '../../../api/robotapi';
import { COLORS } from '../../../config/constants';
import { useWindowBreakpoints } from '../../../hooks/useWindowBreakpoints';

type CurveInput = {
    start?: number;
    mid: number;
    end: number;
};

const SLIDER_CONSTANTS = {
    deposit: {
        initial: {
            mid: 15_000_00,
            end: 100_000_00,
        },
        periodic: {
            mid: 1_500_00,
            end: 10_000_00,
        },
    },
    capitalGoal: {
        amount: {
            mid: 150_000_00,
            end: 1_000_000_00,
        },
        inYears: {
            end: 50, // Max 30 years
        },
    },
};

/* Copied form Stack Overflow: https://stackoverflow.com/questions/7246622/how-to-create-a-slider-with-a-non-linear-scale */
const generateFormula = ({ start = 0, mid, end }: CurveInput) => {
    let x = start;
    let y = mid;
    let z = end;

    let A = (x * z - Math.pow(y, 2)) / (x - 2 * y + z);
    let B = Math.pow(y - x, 2) / (x - 2 * y + z);
    let C = 2 * Math.log((z - y) / (y - x));

    return {
        pointToResult: (X: number) => {
            const result = A + B * Math.exp(C * X);

            const numberOfDigits = (Math.ceil(result) + '').length;
            const decimalPow = Math.pow(10, numberOfDigits - 2);

            return Math.ceil(result / decimalPow) * decimalPow;
        },
        resultToPoint: (Y: number) => {
            return Math.log((Y - A) / B) / C;
        },
    };
};

const capitalGoalCurve = generateFormula(SLIDER_CONSTANTS.capitalGoal.amount);

const periodicDepositCurve = generateFormula(SLIDER_CONSTANTS.deposit.periodic);

const initialDepositCurve = generateFormula(SLIDER_CONSTANTS.deposit.initial);

export const ForecastChartSection: React.FC<{} & HTMLProps<HTMLDivElement>> = ({ children, className, ...rest }) => {
    const { forecast, riskProfiles } = useContext(ForecastContext);
    const { t } = useTranslation('investment');
    const goalIndication = useContext(GoalIndicationContext);
    const breakpoints = useWindowBreakpoints();
    const hideStrategy = !riskProfiles.query.isError && !riskProfiles.query.isLoading;

    const forecastValues = useMemo<ForecastValue[]>(() => {
        return (
            forecast.query.data?.monthlyForecastValues?.median.map((median, i) => {
                return {
                    date: new Date(),
                    pessimistic: forecast.query.data?.monthlyForecastValues?.pessimistic[i] || 0,
                    optimistic: forecast.query.data?.monthlyForecastValues?.optimistic[i] || 0,
                    median: median || 0,
                };
            }) || []
        );
    }, [forecast.query.data]);

    const suggestions = useMemo(() => {
        const suggestedInitial =
            forecast.parameters.deposits.initial.amount + (forecast.query.data?.suggest?.paymentSingleAmount || 0);
        const suggestedPeriodicYearly =
            forecast.parameters.deposits.periodic.amount *
            (forecast.parameters.deposits.periodic.interval === 'monthly' ? 12 : 1) +
            (forecast.query.data?.suggest?.paymentRecurringAmountYearly ||
                forecast.query.data?.suggest?.paymentRecurringAmount ||
                0);

        return {
            initial: suggestedInitial,
            periodicYearly: suggestedPeriodicYearly,
        };
    }, [forecast.query.data]); // eslint-disable-line react-hooks/exhaustive-deps

    const reachesGoal =
        forecast.query.data?.forecastExpirationValues.median &&
        forecast.parameters.capitalGoal.amount <= forecast.query.data?.forecastExpirationValues.median;

    return (
        <div
            className={`p-4 md:p-8 lg:shadow-2xl-all-sides lg:rounded-2xl bg-bnd-white-1 dark:bg-bnd-black-2 ${className}`}
            {...rest}
        >
            <div className="flex flex-row justify-between items-center">
                <span className="font-semibold text-2xl">{t('forecast-screen-title')}</span>
                <div className="opacity-1 flex flex-col sm:flex-row items-center sm:items-center">
                    Lijngrafiek?
                    <Toggle
                        className="ml-0 mt-2 sm:ml-4 sm:mt-0"
                        checked={forecast.parameters.display === 'line' ? true : false}
                        icons={false}
                        onChange={() => {
                            forecast.actions.setParameters({
                                display: forecast.parameters.display === 'bar' ? 'line' : 'bar',
                            });
                        }}
                    />
                </div>
            </div>
            <div className={`mt-8 2xl:grid grid-cols-4 gap-2 lg:gap-6 flex flex-col-reverse`}>
                <div>
                    <div className={`lg:hidden`}>
                        <ForecastResultsSticky />
                    </div>
                    <div className="mt-2 lg:mt-0 2xl:col-span-1 grid 2xl:grid-rows-5 2xl:grid-cols-none  sm:grid-cols-2 sm:grid-rows-3 grid-cols-1 gap-2 sm:gap-6 relative">
                        <Forecast.ParameterCard
                            title={t('forecast-screen-financial-goal')}
                            leadingElement={PathIcon(
                                goalIndication.parameters.selectedGoal,
                                'w-10 h-10 sm:w-12 sm:w-12 text-bnd-green mr-2 2xl:mr-0',
                            )}
                            className={`row-span-1 ${forecast.parameters.capitalGoal.amount > 0 && !reachesGoal
                                    ? 'border-l-4 border-bnd-orange'
                                    : 'border-l-4 border-bnd-green'
                                }`}
                            inputChanger={
                                //Green #00B263 Orange #EABB40
                                <>
                                    <Slider
                                        className="mt-1 sm:mt-1"
                                        handleStyle={{
                                            border: 'none',
                                        }}
                                        max={1}
                                        min={0}
                                        step={0.01}
                                        value={capitalGoalCurve.resultToPoint(forecast.parameters.capitalGoal.amount)}
                                        onChange={(value) => {
                                            const _value = value < 0 ? 0 : value;
                                            forecast.actions.setParameters({
                                                capitalGoal: {
                                                    amount: Math.ceil(capitalGoalCurve.pointToResult(_value)),
                                                },
                                            });
                                        }}
                                    />
                                </>
                            }
                        >
                            <div className="max-w-16 sm:w-full">
                                <CurrencyInput
                                    allowNegativeValue={false}
                                    className="sm:p-1 rounded-lg border w-full text-left sm:text-left text-sm px-1 my-0.5 focus:outline-none"
                                    value={forecast.parameters.capitalGoal.amount / 100}
                                    fixedDecimalLength={0}
                                    decimalsLimit={2}
                                    placeholder="Financieel doel"
                                    intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                                    onValueChange={(value = '', name) => {
                                        let decimal = reverseFormatNumber(value, 'nl-NL');
                                        decimal = +decimal < 0 ? 0 : +decimal;

                                        forecast.actions.setParameters({
                                            capitalGoal: {
                                                amount: Math.ceil(+decimal * 100),
                                            },
                                        });
                                    }}
                                />
                            </div>
                        </Forecast.ParameterCard>
                        <Forecast.ParameterCard
                            title={t('forecast-screen-initial-deposit')}
                            leadingElement={
                                <IconElements.MoneyPile className="w-10 h-10 sm:w-12 sm:h-12 text-bnd-green mr-2 2xl:mr-0" />
                            }
                            className={`row-span-1 ${forecast.parameters.capitalGoal.amount > 0 && !reachesGoal
                                    ? 'border-l-4 border-bnd-orange'
                                    : 'border-l-4 border-bnd-green'
                                }`}
                            inputChanger={
                                <Slider
                                    className="mt-1 sm:mt-1"
                                    handleStyle={{
                                        border: 'none',
                                        backgroundColor:
                                            forecast.parameters.deposits.initial.amount < suggestions.initial
                                                ? COLORS.bndFondsOrange
                                                : COLORS.bndGreen,
                                    }}
                                    max={1}
                                    min={0}
                                    step={0.01}
                                    value={initialDepositCurve.resultToPoint(
                                        forecast.parameters.deposits.initial.amount,
                                    )}
                                    railStyle={{
                                        background:
                                            forecast.parameters.capitalGoal.amount > 0
                                                ? `linear-gradient(90deg, #EABB40 ${initialDepositCurve.resultToPoint(suggestions.initial) * 100
                                                }%, rgba(229, 231, 235, var(--tw-bg-opacity)) 0%)`
                                                : '',
                                    }}
                                    onChange={(value) => {
                                        const _value = value < 0 ? 0 : value;
                                        forecast.actions.setParameters({
                                            deposits: {
                                                initial: {
                                                    amount: Math.ceil(initialDepositCurve.pointToResult(_value)),
                                                },
                                            },
                                        });
                                    }}
                                />
                            }
                        >
                            <div className="max-w-16 sm:w-full">
                                <CurrencyInput
                                    allowNegativeValue={false}
                                    className="sm:p-1 rounded-lg border w-full text-left my-0.5 text-sm sm:text-left px-1 focus:outline-none"
                                    value={forecast.parameters.deposits.initial.amount / 100}
                                    fixedDecimalLength={0}
                                    decimalsLimit={2}
                                    placeholder="Eenmalige inleg"
                                    intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                                    onValueChange={(value = '', name) => {
                                        let decimal = reverseFormatNumber(value, 'nl-NL');
                                        decimal = +decimal < 0 ? 0 : +decimal;
                                        forecast.actions.setParameters({
                                            deposits: {
                                                initial: {
                                                    amount: Math.ceil(+decimal * 100),
                                                },
                                            },
                                        });
                                    }}
                                />
                            </div>
                        </Forecast.ParameterCard>
                        <Forecast.ParameterCard
                            leadingElement={
                                <IconElements.TimeAgenda className="w-10 h-10 sm:w-12 sm:h-12 text-bnd-green mr-2 2xl:mr-0" />
                            }
                            className={`row-span-1 ${forecast.parameters.deposits.periodic.amount > 0 && !reachesGoal
                                    ? 'border-l-4 border-bnd-orange'
                                    : 'border-l-4 border-bnd-green'
                                }`}
                            inputChanger={
                                <Slider
                                    className="mt-1 sm:mt-1"
                                    handleStyle={{
                                        border: 'none',
                                        backgroundColor:
                                            forecast.parameters.deposits.periodic.amount <
                                                suggestions.periodicYearly /
                                                (forecast.parameters.deposits.periodic.interval === 'monthly' ? 12 : 1)
                                                ? COLORS.bndFondsOrange
                                                : COLORS.bndGreen,
                                    }}
                                    max={1}
                                    min={0}
                                    step={0.01}
                                    value={periodicDepositCurve.resultToPoint(
                                        forecast.parameters.deposits.periodic.amount,
                                    )}
                                    railStyle={{
                                        background:
                                            forecast.parameters.capitalGoal.amount > 0
                                                ? `linear-gradient(90deg, #EABB40 ${periodicDepositCurve.resultToPoint(
                                                    suggestions.periodicYearly /
                                                    (forecast.parameters.deposits.periodic.interval ===
                                                        'monthly'
                                                        ? 12
                                                        : 1),
                                                ) * 100
                                                }%, rgba(229, 231, 235, var(--tw-bg-opacity)) 0%)`
                                                : '',
                                    }}
                                    onChange={(value) => {
                                        const _value = value < 0 ? 0 : value;
                                        forecast.actions.setParameters({
                                            deposits: {
                                                periodic: {
                                                    amount: Math.ceil(periodicDepositCurve.pointToResult(_value)),
                                                },
                                            },
                                        });
                                    }}
                                />
                            }
                        >
                            <div className="flex flex-col sm:flex-col w-full justify-between items-start sm:items-start">
                                <span className="relative flex items-center sm:w-full ">
                                    <select
                                        className=" mr-1 pr-5 w-full font-semibold sm:py-1.5 border-0 border-gray-200 rounded-lg focus:outline-none appearance-none bg-opacity-0  cursor-pointer font-xs sm:font-semibold"
                                        value={forecast.parameters.deposits.periodic.interval}
                                        onChange={(e) => {
                                            const value: ForecastParameters['deposits']['periodic']['interval'] =
                                                e.target.value === 'yearly' ? 'yearly' : 'monthly';
                                            forecast.actions.setParameters({
                                                deposits: {
                                                    periodic: {
                                                        interval: value,
                                                    },
                                                },
                                            });
                                        }}
                                    >
                                        <option value="monthly">{t('forecast-screen-periodic-deposit-monthly')}</option>
                                        <option value="yearly">{t('forecast-screen-periodic-deposit-yearly')}</option>
                                    </select>
                                    <IconElements.ChevronDown className="absolute right-1 h-4 w-4 text-black pointer-events-none" />
                                </span>
                                <div className="max-w-16 sm:w-full">
                                    <CurrencyInput
                                        allowNegativeValue={false}
                                        className="sm:p-1 rounded-lg border w-full text-left text-sm  my-0.5 sm:text-left px-1 focus:outline-none"
                                        value={forecast.parameters.deposits.periodic.amount / 100}
                                        fixedDecimalLength={0}
                                        decimalsLimit={2}
                                        placeholder="Periodieke inleg"
                                        intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                                        onValueChange={(value = '', name) => {
                                            let decimal = reverseFormatNumber(value, 'nl-NL');
                                            decimal = +decimal < 0 ? 0 : +decimal;
                                            forecast.actions.setParameters({
                                                deposits: {
                                                    periodic: {
                                                        amount: Math.ceil(+decimal * 100),
                                                    },
                                                },
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        </Forecast.ParameterCard>
                        <Forecast.ParameterCard
                            title={t('forecast-screen-duration')}
                            leadingElement={
                                <IconElements.Clock className="w-10 h-10 sm:w-12 sm:h-12 text-bnd-green mr-2 2xl:mr-0" />
                            }
                            className={`row-span-1 ${forecast.parameters.capitalGoal.amount > 0 && !reachesGoal
                                    ? 'border-l-4 border-bnd-orange'
                                    : 'border-l-4 border-bnd-green'
                                }`}
                            inputChanger={
                                <Slider
                                    className="mt-1 sm:mt-1"
                                    handleStyle={{
                                        border: 'none',
                                    }}
                                    max={1}
                                    min={0}
                                    step={0.01}
                                    value={
                                        forecast.parameters.capitalGoal.inYears /
                                        SLIDER_CONSTANTS.capitalGoal.inYears.end
                                    }
                                    railStyle={
                                        {
                                            // background:
                                            //     forecast.parameters.capitalGoal.amount > 0 && !reachesGoal
                                            //         ? `linear-gradient(90deg, #EABB40 ${
                                            //               (goalAchieveSuggestions.duration /
                                            //                   SLIDER_CONSTANTS.capitalGoal.inYears.end) *
                                            //               100
                                            //           }%, rgba(229, 231, 235, var(--tw-bg-opacity)) 0%)`
                                            //         : '',
                                        }
                                    }
                                    onChange={(value) => {
                                        forecast.actions.setParameters({
                                            capitalGoal: {
                                                inYears: Math.ceil(SLIDER_CONSTANTS.capitalGoal.inYears.end * value),
                                            },
                                        });
                                    }}
                                />
                            }
                        >
                            <div className="font-medium">{forecast.parameters.capitalGoal.inYears} jaar</div>
                        </Forecast.ParameterCard>
                        <Forecast.ParameterCard
                            title={t('forecast-screen-risk-reduction')}
                            leadingElement={
                                <IconElements.SecurityShield className="w-10 h-10 sm:w-12 sm:h-12 text-bnd-green mr-2 2xl:mr-0" />
                            }
                            info={
                                <Tooltip
                                    className="ml-2"
                                    tooltipTitle={t('forecast-screen-risk-reduction')}
                                    tooltipText={t('forecast-screen-risk-reduction-description')}
                                    tooltipId="riskReduction"
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                            }
                            className={`row-span-1 ${forecast.parameters.capitalGoal.amount > 0 && !reachesGoal
                                    ? 'border-l-4 border-bnd-orange'
                                    : 'border-l-4 border-bnd-green'
                                }`}
                            inputChanger={
                                <Toggle
                                    className="mt-1 sm:mt-1"
                                    icons={false}
                                    checked={forecast.parameters.risk.reduction.isEnabled}
                                    onChange={(event) => {
                                        forecast.actions.setParameters({
                                            risk: {
                                                reduction: {
                                                    isEnabled: event.target.checked,
                                                },
                                            },
                                        });
                                    }}
                                />
                            }
                        >
                            <></>
                        </Forecast.ParameterCard>
                    </div>
                </div>
                <div className={`2xl:mt-0 col-span-3 flex-1 flex flex-col`} style={{ minHeight: '500px' }}>
                    <div className={`flex justify-center items-center ${hideStrategy ? '' : 'opacity-0'}`}>
                        {t('forecast-screen-risk-profile-question')}
                        <Tooltip
                            tooltipId="Strategy"
                            tooltipTitle="Risico strategie"
                            tooltipText={t('forecast-screen-risk-profile-tooltip')}
                            className="ml-2"
                            place="bottom"
                        >
                            <TooltipIndicator>?</TooltipIndicator>
                        </Tooltip>
                    </div>
                    <div className="flex flex-col">
                        <div
                            className={`flex flex-row flex-start mt-4 whitespace-nowrap overflow-y-hidden -mx-4 md:-mx-8 ${hideStrategy ? '' : 'opacity-0'
                                }`}
                        >
                            <div className="flex flex-row mx-auto px-4">
                                {riskProfiles.query.data?.data.map((profile, i) => {
                                    return (
                                        <Pill
                                            tint="green"
                                            onClick={() => {
                                                forecast.actions.setParameters({
                                                    risk: {
                                                        profile: {
                                                            id: profile.id + '',
                                                            label: profile.description,
                                                        },
                                                    },
                                                });
                                            }}
                                            isActive={forecast.parameters.risk.profile.id === profile.id + ''}
                                            className={`${i > 0 && 'ml-4'}`}
                                            key={'statpill' + profile.id}
                                        >
                                            {profile.description}
                                        </Pill>
                                    );
                                })}
                            </div>
                        </div>
                        <span className="flex sm:hidden justify-center">
                            <IconElements.DoubleEndedArrow className="w-10 h-10 text-bnd-green" />
                        </span>
                    </div>

                    {breakpoints.isWindowSmallerThan('sm') && forecast.parameters.display === 'line' && (
                        <span className="font-bold text-bnd-orange text-center py-8">
                            Deze grafiek is beter te bekijken op een breder scherm.
                        </span>
                    )}

                    <ForecastChartWrapper
                        inYears={forecast.parameters.capitalGoal.inYears}
                        type="bar"
                        goal={forecast.parameters.capitalGoal.amount}
                        deposit={getTotalDeposit(forecast.parameters)}
                        className="mt-2 sm:mt-8 flex-1"
                        isLoading={forecast.query.isLoading}
                        isError={forecast.query.isError}
                        isFetching={forecast.query.isFetching}
                        isIdle={forecast.query.isIdle}
                        values={forecastValues}
                    ></ForecastChartWrapper>
                </div>
            </div>
        </div>
    );
};
